import React, { useEffect, useMemo, useRef } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import BlotFormatter from 'quill-blot-formatter'

import { CFieldProps } from '@velocity/shared'
import { useField } from 'formik'
import { uploadFile } from '@velocity/shared'
import { toast } from 'react-toastify'

// Register BlotFormatter module for image resizing
Quill.register('modules/blotFormatter', BlotFormatter)

interface PlaceholderItem {
  id: string
  label: string
}
export const RenderContent = ({ content }: { content: string }) => {
  let html = ''
  try {
    const delta = JSON.parse(content || '{}')
    const temp = document.createElement('div')
    const quill = new Quill(temp)
    quill.setContents(delta)
    html = temp.children[0].innerHTML // This becomes your HTML
  } catch (error) {
    html = content
  }

  return (
    <div
      style={{ whiteSpace: 'pre-wrap' }}
      dangerouslySetInnerHTML={{ __html: html }}
    ></div>
  )
}
export default function Editor(props: CFieldProps) {
  const [field, , helpers] = useField(props.name)
  const ref = useRef<any>(null)
  const containerRef = useRef<any>(null)

  const placeholderList: PlaceholderItem[] = useMemo(
    () => props.editorProps?.placeholderList || [],
    [props.editorProps]
  )

  //image processing
  // Insert Image(selected by user) to quill
  const insertToEditor = (url: string) => {
    const range = ref.current?.getSelection()
    if (range) ref.current?.insertEmbed(range.index, 'image', url)
  }

  // Upload Image to Image Server such as AWS S3, Cloudinary, Cloud Storage, etc..
  const saveToServer = async (file: File) => {
    const toastId = toast.loading('Uploading image...')
    try {
      const res = await uploadFile(file, window.location.pathname.split('/')[1])
      if (res.status) insertToEditor(res.data?.url)
      toast.update(toastId, {
        render: 'Image uploaded!',
        type: 'success',
        isLoading: false
      })
    } catch (error) {
      toast.update(toastId, {
        render: 'Failed to upload image',
        type: 'error',
        isLoading: false
      })
    } finally {
      toast.dismiss(toastId)
    }
  }

  // Open Dialog to select Image File
  const selectLocalImage = () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = () => {
      const file = input.files?.[0]
      if (file) saveToServer(file)
    }
  }

  useEffect(() => {
    const container = containerRef.current
    const editorContainer = container?.appendChild(
      container?.ownerDocument.createElement('div')
    )
    const toolbar = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean']
    ]

    // Add placeholder dropdown if placeholders exist
    if (placeholderList.length > 0) {
      toolbar.push([{ placeholder: placeholderList.map((p) => p.id) } as any])
    }
    const quill = new Quill(editorContainer, {
      modules: { toolbar, blotFormatter: {} },
      theme: 'snow'
    })

    ref.current = quill

    quill.clipboard.dangerouslyPasteHTML(field.value || '')
    quill.on('text-change', (delta, oldDelta, source) => {
      helpers.setValue(quill.root.innerHTML)
    })
    ;(quill.getModule('toolbar') as any).addHandler('image', selectLocalImage)

    return () => {
      ref.current = null
      container.innerHTML = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, placeholderList])

  // Generate CSS for placeholder dropdown labels
  useEffect(() => {
    if (placeholderList.length === 0) return

    const styleId = 'quill-placeholder-styles'
    let styleTag = document.getElementById(styleId)

    if (!styleTag) {
      styleTag = document.createElement('style')
      styleTag.id = styleId
      document.head.appendChild(styleTag)
    }

    const css = placeholderList
      .map(
        (p) => `
        .ql-picker.ql-placeholder .ql-picker-item[data-value="${p.id}"]::before {
          content: "${p.label}";
        }
      `
      )
      .join('\n')

    styleTag.textContent =
      css +
      `
        .ql-picker.ql-placeholder {
          width: 130px;
        }
        .ql-picker.ql-placeholder > span.ql-picker-label::before {
          content: 'Merge Fields';
        }
        .ql-picker.ql-placeholder .ql-picker-options {
          max-height: 200px;
          overflow-y: auto;
        }
      `

    return () => {
      // Cleanup on unmount
      const tag = document.getElementById(styleId)
      if (tag) tag.remove()
    }
  }, [placeholderList])

  // Set up placeholder dropdown click handlers
  useEffect(() => {
    if (!ref.current || placeholderList.length === 0) return

    const toolbar = ref.current?.getModule('toolbar') as any
    const toolbarElement = toolbar?.container as HTMLElement
    if (!toolbarElement) return

    const pickerItems = toolbarElement.querySelectorAll(
      '.ql-placeholder .ql-picker-item'
    )

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement
      const value = target.getAttribute('data-value')
      if (!value) return

      // Focus the editor and get selection
      ref.current?.focus()
      const range = ref.current?.getSelection(true)
      const placeholderText = `{{${value}}}`
      ref.current?.insertText(range.index, placeholderText)
      ref.current?.setSelection(range.index + placeholderText.length)

      // Reset the dropdown to show "Merge Fields" again
      const pickerLabel = toolbarElement.querySelector(
        '.ql-placeholder .ql-picker-label'
      )
      if (pickerLabel) {
        pickerLabel.setAttribute('data-value', '')
      }
    }

    pickerItems.forEach((item) => {
      item.addEventListener('click', handleClick)
    })

    return () => {
      pickerItems.forEach((item) => {
        item.removeEventListener('click', handleClick)
      })
    }
  }, [placeholderList])

  return (
    <div style={{ minHeight: 200, backgroundColor: 'white' }}>
      <div ref={containerRef} />
    </div>
  )
}
